// Routes Atlas page uses. All of them are served (router.ts and src/aithora/v3/companion/);
// (the page shows each block's empty state and says what is missing until they are).
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  CompanionDirectivesResponse,
  CompanionInstructions,
  CompanionMe,
  CompanionMemory,
  CompanionMemoryPeople,
  CompanionPatterns,
  CompanionQuiet,
  CompanionResidentsResponse,
  CompanionRoster,
  CompanionRound,
  CompanionSessionResponse,
  CompanionStartersResponse,
  CompanionSummary,
  CompanionToday,
  CompanionTrace,
  CompanionView,
  CompanionFamilyView,
  CompanionMomentCreated,
  CompanionMoments,
  CompanionFirstTalkPatch,
  CompanionFirstTalk,
  CompanionUnsafeSignal,
  CompanionHeldDecision,
  CompanionHeldResponse,
  CompanionFamilyResidents,
} from './companion-types';

const A = AITHORA_PREFIX;

// ── REAL ────────────────────────────────────────────────────────────────────

/** GET /companion/view: the caller's framing (resident, family, staff, internal). */
export const companionView = route<CompanionView>()('GET', `${A}/companion/view`);

/** GET /residents/:id/companion/session: open conversation right now, no content. */
export const companionSession = route<CompanionSessionResponse>()('GET', `${A}/residents/:id/companion/session`);

/** GET /residents/:id/companion/starters: preferred name, curated starters, interests. */
export const companionStarters = route<CompanionStartersResponse>()('GET', `${A}/residents/:id/companion/starters`);

/** GET /residents/:id/companion-directives: the care team's instructions for one resident. */
export const companionDirectives = route<CompanionDirectivesResponse>()('GET', `${A}/residents/:id/companion-directives`);

/** POST /residents/:id/companion-directives {directive, expiresAt?}: staff set an instruction. */
export const companionDirectiveAdd = route<{ ok: true; id: string }, void, { directive: string; expiresAt?: string }>()(
  'POST', `${A}/residents/:id/companion-directives`);

/** DELETE /residents/:id/companion-directives/:did: stop an instruction (soft, ledgered). */
export const companionDirectiveStop = route<{ ok: true }>()('DELETE', `${A}/residents/:id/companion-directives/:did`);

/** GET /residents: the building's residents (staff; scoped by the server). */
export const companionResidents = route<CompanionResidentsResponse>()('GET', `${A}/residents`);

/** GET /oversight/held: turns and actions held for a person (Aithora, Facility Admin). */
export const companionHeld = route<CompanionHeldResponse, { limit?: number; facilities?: string }>()('GET', `${A}/oversight/held`, { honest: true });

/** POST /oversight/held/:id/decision {decision}: a named person decides a held turn. */
export const companionHeldDecide = route<CompanionHeldDecision, void, { decision: 'allow' | 'refuse' | 'route'; reason?: string }>()(
  'POST', `${A}/oversight/held/:id/decision`, { honest: true });

/** GET /residents/:id/onboarding: the resident's first talk, resumable. */
export const companionFirstTalk = route<CompanionFirstTalk>()('GET', `${A}/residents/:id/onboarding`, { honest: true });

/** PUT /residents/:id/onboarding: one step forward, the preferred name, bigger words. */
export const companionFirstTalkSave = route<CompanionFirstTalk, void, CompanionFirstTalkPatch>()('PUT', `${A}/residents/:id/onboarding`, { honest: true });

/** POST /residents/:id/onboarding/practice-help: the practice help call. Pages nobody. */
export const companionFirstTalkPractice = route<{ real: true; practice: true; paged: false; message: string }>()(
  'POST', `${A}/residents/:id/onboarding/practice-help`, { honest: true });

/** POST /guardian/signals: "I don't feel safe" from the resident's own screen. */
export const companionUnsafe = route<CompanionUnsafeSignal, void, { kind?: string; residentId?: string }>()(
  'POST', `${A}/guardian/signals`, { honest: true });

/** GET /family/my-residents: who a family member is linked to. */
export const companionFamilyResidents = route<CompanionFamilyResidents>()('GET', `${A}/family/my-residents`);

/** GET /family/:residentId/moments: what family sent, and when she saw it. */
export const companionMoments = route<CompanionMoments>()('GET', `${A}/family/:residentId/moments`, { honest: true });

/** POST /family/:residentId/moments {mediaType, caption?, contentType?, byteSize?} */
export const companionMomentSend = route<CompanionMomentCreated, void, { mediaType: 'photo' | 'voice' | 'video' | 'text'; caption?: string; contentType?: string; byteSize?: number }>()(
  'POST', `${A}/family/:residentId/moments`, { honest: true });

/**
 * POST /chat {message, userId, organizationId, conversationId?}: answered as a stream
 * (text/event-stream, `data: {"text"}` frames, then `data: [DONE]`), so it is read with
 * client.raw (see readChatStream in the console). The path is kept here with the rest.
 */
export const CHAT_PATH = `${A}/chat`;

/**
 * GET /companion/turns/stream: SSE `companion.turn` frames, data {resident_id, resident_first,
 * session_state, at}, for the caller's buildings (staff only). Read with client.raw.
 */
export const COMPANION_TURN_STREAM_PATH = `${A}/companion/turns/stream`;

// ── Served by src/aithora/v3/companion/ (Console V3 people pages) ──────────────

/** GET /companion/summary: the headline figures for the caller's scope. */
export const companionSummary = route<CompanionSummary, { facilities?: string }>()('GET', `${A}/companion/summary`, { honest: true });

/** GET /companion/roster: every enrolled resident in scope, alerts first. */
export const companionRoster = route<CompanionRoster, { facilities?: string }>()('GET', `${A}/companion/roster`, { honest: true });

/** GET /residents/:id/companion/today: today's turns with their gate record, summary first. */
export const companionToday = route<CompanionToday>()('GET', `${A}/residents/:id/companion/today`, { honest: true });

/** POST /residents/:id/companion/transcript {reason}: open today's transcript. Logged under the caller's name. */
export const companionTranscriptOpen = route<CompanionToday, void, { reason: string }>()(
  'POST', `${A}/residents/:id/companion/transcript`, { honest: true });

/** A past Atlas thread (no words): restored from the Flutter previous-conversations browser. */
export interface CompanionPastThread {
  id: string;
  startedAt: string | null;
  endedAt: string | null;
  messageCount: number;
}
/**
 * GET /residents/:id/companion/conversations: the resident's earlier threads. The building's
 * own care team only, after opening today's transcript with a reason (403 otherwise).
 */
export const companionPastThreads = route<{ real: true; conversations: CompanionPastThread[] }>()('GET', `${A}/residents/:id/companion/conversations`);
/** GET /residents/:id/companion/conversations/:cid: one earlier thread's turns (same gate, audited). */
export const companionPastThread = route<{ real: true; conversationId: string; startedAt: string | null; messages: { who: 'resident' | 'companion'; text: string; at: string | null }[] }>()(
  'GET', `${A}/residents/:id/companion/conversations/:cid`);

/** POST /residents/:id/companion/next-step {queued}: put the suggested step on a round (or send it to the floor). */
export const companionNextStep = route<{ real: boolean; queued: boolean; worklistItemId: string | null }, void, { queued: boolean }>()(
  'POST', `${A}/residents/:id/companion/next-step`, { honest: true });

/** GET /companion/memory/people: residents whose memory the caller may read. */
export const companionMemoryPeople = route<CompanionMemoryPeople>()('GET', `${A}/companion/memory/people`, { honest: true });

/** GET /residents/:id/companion/memory: every fact, with where it came from. */
export const companionMemory = route<CompanionMemory>()('GET', `${A}/residents/:id/companion/memory`, { honest: true });

/** PATCH /residents/:id/companion/memory/:factId {detail?, muted?}: correct or mute a fact (ledgered). */
export const companionMemoryPatch = route<{ real: boolean; fact: import('./companion-types').CompanionMemoryFact }, void, { detail?: string; muted?: boolean }>()(
  'PATCH', `${A}/residents/:id/companion/memory/:factId`, { honest: true });

/** DELETE /residents/:id/companion/memory/:factId: forget a fact (ledgered, undoable). */
export const companionMemoryForget = route<{ real: boolean; forgotten: true }>()('DELETE', `${A}/residents/:id/companion/memory/:factId`, { honest: true });

/** POST /residents/:id/companion/memory/:factId/restore: "Put it back". */
export const companionMemoryRestore = route<{ real: boolean; restored: true }>()('POST', `${A}/residents/:id/companion/memory/:factId/restore`, { honest: true });

/** POST /residents/:id/companion/memory {text}: staff tell Atlas something, with their name on it. */
export const companionMemoryTell = route<{ real: boolean; factId: string }, void, { text: string }>()(
  'POST', `${A}/residents/:id/companion/memory`, { honest: true });

/** GET /residents/:id/companion/trace: the reasoning record, newest first. */
export const companionTrace = route<CompanionTrace>()('GET', `${A}/residents/:id/companion/trace`, { honest: true });

/** GET /companion/patterns: refusal reasons that repeat this week. */
export const companionPatterns = route<CompanionPatterns>()('GET', `${A}/companion/patterns`, { honest: true });

/** GET /companion/quiet: residents Atlas has not reached, longest first. */
export const companionQuiet = route<CompanionQuiet>()('GET', `${A}/companion/quiet`, { honest: true });

/** GET /companion/round: Care Staff, what changed before the round. */
export const companionRound = route<CompanionRound>()('GET', `${A}/companion/round`, { honest: true });

/** POST /companion/round/:residentId/worklist: add a round row to the caller's worklist. */
export const companionRoundToWorklist = route<{ real: boolean; worklistItemId: string }>()(
  'POST', `${A}/companion/round/:residentId/worklist`, { honest: true });

/** GET /companion/instructions: every instruction in force in the building. */
export const companionInstructions = route<CompanionInstructions>()('GET', `${A}/companion/instructions`, { honest: true });

/** POST /residents/:id/companion-directives/:did/renew: another 90 days. */
export const companionDirectiveRenew = route<{ real: boolean; expiresAt: string }>()(
  'POST', `${A}/residents/:id/companion-directives/:did/renew`, { honest: true });

/** GET /companion/me: the resident's or member's own page (raised today, sharing). */
export const companionMe = route<CompanionMe>()('GET', `${A}/companion/me`, { honest: true });

/** PUT /companion/me/sharing {personId, on}: the resident turns one person on or off. */
export const companionSharingSet = route<{ real: boolean; on: boolean }, void, { personId: string; on: boolean }>()(
  'PUT', `${A}/companion/me/sharing`, { honest: true });

/**
 * POST /residents/:id/call-nurse: "Call a nurse" from the person's own screen. In a
 * building it is a care request for the staff on shift. At home (an Aithora Home member)
 * there is no nurse: it goes to the member's care circle on their app and to Aithora's
 * at-home queue, and `told` names who was told. A second press inside ten minutes is the
 * same request (repeat: true).
 */
export const companionCallNurse = route<{
  real: boolean; requestId: string; atHome?: boolean; repeat?: boolean; told?: { who: string; what: string }[];
  /** In a building: OPEN (her nurse has been told) or ACKNOWLEDGED (a nurse is coming). */
  status?: string | null; acknowledgedAt?: string | null;
  /** What her screen says: "Your nurse has been told." then "A nurse is coming." */
  line?: string | null;
}>()('POST', `${A}/residents/:id/call-nurse`, { honest: true });

/** GET /residents/:id/call-nurse: her latest call in a building, for her own screen to follow. */
export const companionCallNurseStatus = route<{ real: boolean; requestId: string | null; status: string | null; acknowledgedAt: string | null; line: string | null }>()(
  'GET', `${A}/residents/:id/call-nurse`, { honest: true });

/** GET /family/:residentId/companion: the family view (only what she agreed to share). */
export const companionFamily = route<CompanionFamilyView>()('GET', `${A}/family/:residentId/companion`, { honest: true });

/** POST /family/:residentId/companion/ask {key}: ask her, on her own screen, whether to share one thing. */
export const companionFamilyAsk = route<{ real: boolean; askedAt: string }, void, { key: string }>()(
  'POST', `${A}/family/:residentId/companion/ask`, { honest: true });

/**
 * The caller's answer to sending what they say or write to our AI providers (App Store
 * guideline 5.1.2(i)). null: not asked yet, or asked under an older wording of the notice.
 * Served by src/aithora/v3/ai-consent.ts.
 */
export interface AiConsent {
  decision: 'agreed' | 'declined' | null;
  noticeVersion: string;
  decidedAt: string | null;
}

/** GET /me/ai-consent: the caller's own standing answer. */
export const aiConsent = route<AiConsent>()('GET', `${A}/me/ai-consent`);

/** PUT /me/ai-consent {agreed, platform?}: Agree (true) or Not now (false), kept on record. */
export const aiConsentSave = route<AiConsent, void, { agreed: boolean; platform?: 'ios' | 'android' | 'web' }>()(
  'PUT', `${A}/me/ai-consent`);

export const companionApi = {
  aiConsent,
  aiConsentSave,
  companionView,
  companionSession,
  companionStarters,
  companionDirectives,
  companionDirectiveAdd,
  companionDirectiveStop,
  companionResidents,
  companionHeld,
  companionHeldDecide,
  companionFirstTalk,
  companionFirstTalkSave,
  companionFirstTalkPractice,
  companionUnsafe,
  companionFamilyResidents,
  companionMoments,
  companionMomentSend,
  companionSummary,
  companionRoster,
  companionToday,
  companionTranscriptOpen,
  companionNextStep,
  companionMemoryPeople,
  companionMemory,
  companionMemoryPatch,
  companionMemoryForget,
  companionMemoryRestore,
  companionMemoryTell,
  companionTrace,
  companionPatterns,
  companionQuiet,
  companionRound,
  companionRoundToWorklist,
  companionInstructions,
  companionDirectiveRenew,
  companionMe,
  companionSharingSet,
  companionCallNurse,
  companionCallNurseStatus,
  companionFamily,
  companionFamilyAsk,
} as const;
